import { Borders, Workbook, Worksheet } from "exceljs"
import { split } from "lodash"
import moment from "moment"
import { BalanceSheetReportType } from "pages/Finance/Report/types"

type HandleExportExcel = {
  comparisonDates: any[]
  periodValue?: string
  data: BalanceSheetReportType[]
  companyName?: string
  date?: string
}

export const handleExportExcelBalanceSheet = async ({ comparisonDates, data, date, companyName }: HandleExportExcel) => {
  const workbook = new Workbook()
  const worksheet = workbook.addWorksheet("Sheet 1")
  const parents = data.filter((item) => item.parent_id === -1)
  const columns = ["ACCOUNT", "NAME"].concat(comparisonDates.map((item) => moment(item).format("DD MMM YYYY").toUpperCase()))
  worksheet.views = [{ showGridLines: false }]

  worksheet.mergeCells(2, 1, 1, columns.length)

  makeTitle(worksheet, companyName, date)

  const headers = worksheet.addRow(columns)
  headers.font = { bold: true }
  headers.eachCell((cell) => {
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFD700" }
    }
    cell.border = {
      top: { style: "thin", color: { argb: "000000" } },
      left: { style: "thin", color: { argb: "000000" } },
      bottom: { style: "thin", color: { argb: "000000" } },
      right: { style: "thin", color: { argb: "000000" } }
    }
  })

  const mapData = parents?.map((item) => {
    const hierarchy = buildHierarchy(data, item.coa_id)
    return { ...item, children: hierarchy }
  })

  makeRows(mapData, worksheet)

  await workbook.xlsx.writeBuffer().then((buffer) => {
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `balance_sheet_${columns.map((item) => split(item, " ").join("-")).join("_")}.xlsx`
    a.click()
    URL.revokeObjectURL(url)
  })
}

// Helpers
export const buildHierarchy = (data: BalanceSheetReportType[], parentId: number | null): BalanceSheetReportType[] => {
  return data
    .filter((item) => item?.parent_id === parentId)
    .map((item) => ({ ...item, children: buildHierarchy(data, item.coa_id) }))
}

export const makeTitle = (worksheet: Worksheet, companyName?: string, date?: string) => {
  const titleCell = worksheet.getCell(1, 2)
  titleCell.value = "BALANCE SHEET"

  titleCell.alignment = {
    vertical: "middle",
    horizontal: "left",
    wrapText: true
  }
  titleCell.font = { bold: true, size: 15 }

  worksheet.getColumn(2).width = 20

  const companyNameCell = worksheet.addRow([companyName?.toUpperCase()])
  const dateCell = worksheet.addRow([`As at ${moment(date).locale("en-us").format("DD MMMM YYYY")}`])
  const currentRowIdx = worksheet.rowCount
  const endColumnIdx = worksheet.columnCount

  worksheet.mergeCells(currentRowIdx, 1, currentRowIdx + 1, endColumnIdx)

  companyNameCell.eachCell((cell) => {
    cell.alignment = {
      vertical: "middle",
      horizontal: "left",
      wrapText: true
    }
    cell.font = { size: 12 }
  })

  dateCell.eachCell((cell) => {
    cell.alignment = {
      vertical: "middle",
      horizontal: "left",
      wrapText: true
    }
    cell.font = { size: 12 }
  })
}

export const makeRows = (data: BalanceSheetReportType[], worksheet: Worksheet) => {
  const cellBorder: Partial<Borders> = {
    top: { style: "thin", color: { argb: "000000" } },
    left: { style: "thin", color: { argb: "000000" } },
    bottom: { style: "thin", color: { argb: "000000" } },
    right: { style: "thin", color: { argb: "000000" } }
  }

  data.forEach((item: BalanceSheetReportType) => {
    const indent = " ".repeat(item.coa_level * 4)
    
    let isBold = false

    const alwaysBold = ["NET ASSETS", "RAW MATERIAL SPA"]

    if (alwaysBold.includes(item.coa_name)) {
      isBold = true 
    } else if (item.children && item.children.length > 0) {
      isBold = true
    }

    let values: (string | number)[] = []

    if (item.children && item.children.length > 0) {
      values = [item.coacode, `${indent}${item.coa_name}`, ...item.balance.map(() => "")]
    } else {
      values = [item.coacode, `${indent}${item.coa_name}`, ...item.balance]
    }

    const itemRow = worksheet.addRow(values)

    if (isBold) itemRow.font = { bold: true }

    itemRow.eachCell((cell) => (cell.border = cellBorder))

    if (item?.children && item?.children?.length > 0) {
      makeRows(item?.children, worksheet)

      if (!alwaysBold.includes(item?.coa_name)) {
        const totalRow = worksheet.addRow(["", `${indent}Total ${item?.coa_name}`, ...item?.balance])
        totalRow.font = { bold: true }
        totalRow.eachCell((cell) => (cell.border = cellBorder))
      }
    }
  })
}
